/**
 * NewPropertiesPanel — detail pane for reviewing and editing new properties
 * that will be created during this CSV import.
 *
 * Properties are grouped by entity type. Each property renders an expandable
 * accordion card containing the shared PropertyFormFields — the same form
 * fields used in the CRM create-property flow (PropertyTypeSlideout).
 *
 * Uses the library Accordion components (react-aria-based) for accessible,
 * animated expand/collapse — consistent with FileMappingSection and
 * CategorySection elsewhere in the CSV import flow.
 */

import { AlertCircle, File06 } from '@untitledui/icons';
import { memo, useCallback, useEffect, useMemo, useRef } from 'react';

import {
  Accordion,
  AccordionChevron,
  AccordionItem,
  AccordionPanel,
  AccordionTrigger,
} from '@/lib/base/accordion';
import { Button } from '@/lib/base/buttons/button';
import type { PropertyFormValues } from '@/lib/crm/components/properties/PropertyFormFields';
import { PropertyFormFields } from '@/lib/crm/components/properties/PropertyFormFields';
import type { SelectOption } from '@/lib/crm/components/properties/types';
import { useFieldTypes } from '@/lib/crm/hooks/useFieldTypes';
import { getFieldTypeItems } from '@/lib/crm/utils/fieldTypeHelpers';
import { getEntityTypeIconComponent } from '@/lib/crm/utils/icons';
import { humanize } from '@/utils/text';

import { getEntityTypeLabel } from '../../utils/entityTypeLabels';
import { propertyIndexKey } from './mapping-constants';

// =============================================================================
// Types
// =============================================================================

/** A single file→column usage of a new property. */
export interface NewPropertyUsage {
  /** Real file ID (number or string) for sidebar navigation. */
  fileId: string | number;
  filename: string;
  entityTypeName: string;
  column: string;
}

interface NewPropertyUsageRowProps {
  usage: NewPropertyUsage;
  onNavigateToFile?: (fileId: string | number) => void;
}

/**
 * One "Used in" row: the file this property came from, and the column within it.
 *
 * Extracted rather than inlined in the `.map()` so the navigate handler can be a
 * stable `useCallback` — an inline `() => onNavigateToFile(u.fileId)` allocates a
 * new function per usage per render.
 */
function NewPropertyUsageRow({
  usage,
  onNavigateToFile,
}: NewPropertyUsageRowProps) {
  const handleNavigate = useCallback(() => {
    onNavigateToFile?.(usage.fileId);
  }, [onNavigateToFile, usage.fileId]);

  return (
    <li className='flex items-center gap-1.5 text-xs text-fg-secondary'>
      <File06 className='size-3 text-fg-quaternary shrink-0' />
      <span className='truncate'>
        {onNavigateToFile ? (
          <Button
            type='button'
            color='link-color'
            size='sm'
            onClick={handleNavigate}
            className='!h-auto !min-h-0 !p-0 !text-xs !font-medium'
          >
            {usage.filename}
          </Button>
        ) : (
          <span className='font-medium'>{usage.filename}</span>
        )}
        <span className='text-fg-quaternary mx-1'>→</span>
        <span>&ldquo;{usage.column}&rdquo;</span>
      </span>
    </li>
  );
}

export interface NewPropertyItem {
  propertyName: string;
  label: string;
  fieldType: string;
  entityTypeName: string;
  /**
   * Every file that carries an entry for this (property, entity type), in
   * first-seen order (user-edited entries before AI suggestions). One card is
   * rendered per (property, entity type) because the import creates exactly one
   * property per that key; an edit on the card is fanned out to each of these
   * files' entries so no file's copy diverges from what the user sees.
   */
  fileIds: Array<string | number>;
  description?: string;
  options?: SelectOption[];
  isUnique?: boolean;
  /** CSV column this property was created from. */
  sourceColumn?: string;
  /** All file→entity type→column usages of this property. */
  usages?: NewPropertyUsage[];
}

interface NewPropertiesPanelProps {
  /** All new properties across files, already deduped and priority-sorted */
  properties: NewPropertyItem[];
  /** Called when any property field changes */
  onPropertyChange: (
    propertyName: string,
    entityTypeName: string,
    values: PropertyFormValues
  ) => void;
  /** When set, auto-expand this property's accordion card and scroll it into view. */
  initialExpandedProperty?: string | null;
  /**
   * Entity type of the property named by `initialExpandedProperty`. When two
   * entity types share a property-name slug, this disambiguates which card to
   * expand — omitted, the first card matching `initialExpandedProperty` wins.
   */
  initialExpandedEntityType?: string | null;
  /** Called when the user clicks "Generate from CSV" on a select property. */
  onGenerateOptions?: (propertyName: string, entityTypeName: string) => void;
  /** Called when the user clicks a filename in the "Used in" list. */
  onNavigateToFile?: (fileId: string | number) => void;
}

// =============================================================================
// PropertyCard
// =============================================================================

interface PropertyCardProps {
  property: NewPropertyItem;
  onPropertyChange: (
    propertyName: string,
    entityTypeName: string,
    values: PropertyFormValues
  ) => void;
  fieldTypeItems: ReturnType<typeof getFieldTypeItems>;
  fieldTypesLoading: boolean;
  onGenerateOptions?: (propertyName: string, entityTypeName: string) => void;
  /** When true, scroll this card into view after mount (used by auto-expand). */
  shouldScrollIntoView?: boolean;
  /** Called when the user clicks a filename in the "Used in" list. */
  onNavigateToFile?: (fileId: string | number) => void;
}

const PropertyCard = memo(function PropertyCard({
  property,
  onPropertyChange,
  fieldTypeItems,
  fieldTypesLoading,
  onGenerateOptions,
  shouldScrollIntoView,
  onNavigateToFile,
}: PropertyCardProps) {
  const cardRef = useRef<HTMLDivElement>(null);

  const prevOptionsLenRef = useRef(property.options?.length ?? 0);

  useEffect(() => {
    if (!shouldScrollIntoView) {
      return;
    }
    // Delay scroll so the accordion expand animation has started and the
    // panel content is laid out. Then scroll the full card into view.
    const timer = setTimeout(() => {
      cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 250);
    return () => clearTimeout(timer);
  }, [shouldScrollIntoView]);

  // Scroll the card into view when options are populated (e.g. after
  // "Generate options from CSV"). A short delay lets the DOM update with
  // the new options list before we measure scroll position.
  useEffect(() => {
    const prev = prevOptionsLenRef.current;
    const curr = property.options?.length ?? 0;
    prevOptionsLenRef.current = curr;

    if (prev === 0 && curr > 0) {
      const timer = setTimeout(() => {
        cardRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [property.options?.length]);

  const fieldTypeLabel =
    fieldTypeItems.find(i => i.id === property.fieldType)?.label ??
    humanize(property.fieldType);

  const hasOptionsWarning =
    property.fieldType === 'select' &&
    (!property.options || property.options.length < 2);

  const hasEmptyLabel = property.label.trim() === '';

  // Build a concise provenance hint for the collapsed trigger row. When
  // usages are available (multi-file aware), prefer that. Fall back to
  // the simple sourceColumn otherwise.
  const { usages } = property;
  const provenanceHint = useMemo(() => {
    if (usages && usages.length > 0) {
      if (usages.length === 1) {
        const u = usages[0];
        return `from “${u.column}” in ${u.filename}`;
      }
      const uniqueFiles = new Set(usages.map(u => u.filename));
      if (uniqueFiles.size > 1) {
        return `used in ${uniqueFiles.size} files`;
      }
      return `from ${usages.length} columns in ${usages[0].filename}`;
    }
    if (property.sourceColumn) {
      return `from “${property.sourceColumn}”`;
    }
    return null;
  }, [usages, property.sourceColumn]);

  const formValues = useMemo<PropertyFormValues>(
    () => ({
      label: property.label,
      description: property.description ?? '',
      fieldType: property.fieldType,
      options: property.options ?? [],
      isUnique: property.isUnique ?? false,
    }),
    [
      property.label,
      property.description,
      property.fieldType,
      property.options,
      property.isUnique,
    ]
  );

  const handleChange = useCallback(
    (values: PropertyFormValues) => {
      onPropertyChange(property.propertyName, property.entityTypeName, values);
    },
    [onPropertyChange, property.propertyName, property.entityTypeName]
  );

  const handleGenerateOptions = useCallback(() => {
    onGenerateOptions?.(property.propertyName, property.entityTypeName);
  }, [onGenerateOptions, property.propertyName, property.entityTypeName]);

  return (
    <div ref={cardRef}>
      <AccordionItem
        id={propertyIndexKey(property.propertyName, property.entityTypeName)}
        aria-label={`${property.label} property`}
        className={
          hasOptionsWarning
            ? 'border border-[var(--color-error-300)] rounded-lg bg-bg-primary overflow-hidden ring-1 ring-[var(--color-error-200)]'
            : 'border border-border-secondary rounded-lg bg-bg-primary overflow-hidden'
        }
      >
        <AccordionTrigger className='w-full px-3 py-2.5 flex items-center gap-2 hover:bg-bg-secondary text-left'>
          <AccordionChevron variant='swap' className='text-fg-quaternary' />
          <span className='flex-1 min-w-0 truncate'>
            <span className='text-sm font-medium text-fg-primary'>
              {property.label}
            </span>
            {provenanceHint && (
              <span className='ml-1.5 text-xs text-fg-quaternary'>
                {provenanceHint}
              </span>
            )}
          </span>
          {hasOptionsWarning && (
            <span
              className='inline-flex items-center gap-1 text-xs text-[var(--color-error-600)] flex-shrink-0'
              title='Add 2 or more options to save changes.'
            >
              <AlertCircle className='size-3.5' />
              Needs 2+ options
            </span>
          )}
          {hasEmptyLabel && (
            <span
              className='inline-flex items-center gap-1 text-xs text-[var(--color-error-600)] flex-shrink-0'
              title='Enter a label so this property can be created.'
            >
              <AlertCircle className='size-3.5' />
              Label required
            </span>
          )}
          <span className='text-xs text-fg-quaternary flex-shrink-0'>
            {fieldTypeLabel}
          </span>
        </AccordionTrigger>

        <AccordionPanel className='border-t border-border-secondary px-3 py-3'>
          {usages && usages.length > 0 && (
            <div className='mb-3 pb-3 border-b border-border-secondary'>
              <p className='text-xs font-medium text-fg-tertiary mb-1.5'>
                Used in
              </p>
              <ul className='space-y-1'>
                {usages.map(u => (
                  <NewPropertyUsageRow
                    key={`${u.filename}-${u.entityTypeName}-${u.column}`}
                    usage={u}
                    onNavigateToFile={onNavigateToFile}
                  />
                ))}
              </ul>
            </div>
          )}
          <PropertyFormFields
            values={formValues}
            onChange={handleChange}
            fieldTypeItems={fieldTypeItems}
            fieldTypesLoading={fieldTypesLoading}
            showDescription={false}
            showUnique={false}
          />
          {property.fieldType === 'select' && onGenerateOptions && (
            <div className='mt-3 pt-3 border-t border-border-secondary'>
              <Button
                type='button'
                color='secondary'
                size='sm'
                onClick={handleGenerateOptions}
              >
                Generate options from CSV
              </Button>
            </div>
          )}
        </AccordionPanel>
      </AccordionItem>
    </div>
  );
});

// =============================================================================
// EntityTypeGroup
// =============================================================================

interface EntityTypeGroupProps {
  entityTypeName: string;
  properties: NewPropertyItem[];
  onPropertyChange: (
    propertyName: string,
    entityTypeName: string,
    values: PropertyFormValues
  ) => void;
  fieldTypeItems: ReturnType<typeof getFieldTypeItems>;
  fieldTypesLoading: boolean;
  onGenerateOptions?: (propertyName: string, entityTypeName: string) => void;
  /** Property to auto-expand on mount (passed as defaultExpandedKeys to Accordion). */
  initialExpandedProperty?: string | null;
  /** Entity type of `initialExpandedProperty` — disambiguates colliding slugs. */
  initialExpandedEntityType?: string | null;
  /** Called when the user clicks a filename in the "Used in" list. */
  onNavigateToFile?: (fileId: string | number) => void;
}

function EntityTypeGroup({
  entityTypeName,
  properties,
  onPropertyChange,
  fieldTypeItems,
  fieldTypesLoading,
  onGenerateOptions,
  initialExpandedProperty,
  initialExpandedEntityType,
  onNavigateToFile,
}: EntityTypeGroupProps) {
  const Icon = getEntityTypeIconComponent(entityTypeName);
  const label = getEntityTypeLabel(entityTypeName);

  // Find the target property to auto-expand, if it belongs to this group. When
  // initialExpandedEntityType is set, it must also match this group's entity
  // type — otherwise a colliding slug in another entity type's group would be
  // matched instead. When omitted, fall back to matching by name only.
  const targetProperty = useMemo(() => {
    if (!initialExpandedProperty) {
      return undefined;
    }
    return properties.find(
      p =>
        p.propertyName === initialExpandedProperty &&
        (initialExpandedEntityType == null ||
          p.entityTypeName === initialExpandedEntityType)
    );
  }, [initialExpandedProperty, initialExpandedEntityType, properties]);

  const defaultExpandedKeys = useMemo(() => {
    if (!targetProperty) {
      return undefined;
    }
    return [
      propertyIndexKey(
        targetProperty.propertyName,
        targetProperty.entityTypeName
      ),
    ];
  }, [targetProperty]);

  return (
    <div>
      <div className='flex items-center gap-2 mb-3'>
        <Icon className='size-4 text-fg-tertiary' />
        <h3 className='text-sm font-medium text-fg-primary'>{label}</h3>
        <span className='text-xs rounded-full px-1.5 py-0.5 bg-bg-tertiary text-fg-quaternary'>
          {properties.length}
        </span>
      </div>
      <Accordion
        allowsMultipleExpanded
        defaultExpandedKeys={defaultExpandedKeys}
        className='space-y-2'
      >
        {properties.map(prop => (
          <PropertyCard
            key={propertyIndexKey(prop.propertyName, prop.entityTypeName)}
            property={prop}
            onPropertyChange={onPropertyChange}
            fieldTypeItems={fieldTypeItems}
            fieldTypesLoading={fieldTypesLoading}
            onGenerateOptions={onGenerateOptions}
            shouldScrollIntoView={prop === targetProperty}
            onNavigateToFile={onNavigateToFile}
          />
        ))}
      </Accordion>
    </div>
  );
}

// =============================================================================
// NewPropertiesPanel (main export)
// =============================================================================

export function NewPropertiesPanel({
  properties,
  onPropertyChange,
  initialExpandedProperty,
  initialExpandedEntityType,
  onGenerateOptions,
  onNavigateToFile,
}: NewPropertiesPanelProps) {
  const { data: fieldTypes = [], isLoading: fieldTypesLoading } =
    useFieldTypes();

  const fieldTypeItems = useMemo(
    () => getFieldTypeItems(fieldTypes),
    [fieldTypes]
  );

  // Resolve the auto-expand target to a single entity type. When the caller
  // supplies initialExpandedEntityType, honor it. When it's omitted and
  // multiple entity types share the property slug, pick the globally first
  // matching property's entity type so exactly one card expands — otherwise
  // every EntityTypeGroup that contains the slug would expand its own card
  // (see #11395). Returns null when there is nothing to expand.
  const resolvedExpandedEntityType = useMemo(() => {
    if (!initialExpandedProperty) {
      return null;
    }
    if (initialExpandedEntityType != null) {
      return initialExpandedEntityType;
    }
    const firstMatch = properties.find(
      p => p.propertyName === initialExpandedProperty
    );
    return firstMatch?.entityTypeName ?? null;
  }, [initialExpandedProperty, initialExpandedEntityType, properties]);

  // Group properties by entity type, preserving order
  const grouped = useMemo(() => {
    const groups: Array<{
      entityTypeName: string;
      properties: NewPropertyItem[];
    }> = [];
    const groupMap = new Map<string, NewPropertyItem[]>();

    for (const prop of properties) {
      let group = groupMap.get(prop.entityTypeName);
      if (!group) {
        group = [];
        groupMap.set(prop.entityTypeName, group);
        groups.push({ entityTypeName: prop.entityTypeName, properties: group });
      }
      group.push(prop);
    }
    return groups;
  }, [properties]);

  if (properties.length === 0) {
    return (
      <div className='rounded-lg bg-bg-secondary p-6 text-center text-sm text-fg-tertiary'>
        No new properties will be created in this import.
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      <div>
        <h2 className='text-base font-semibold text-fg-primary'>
          New properties
        </h2>
        <p className='mt-1 text-sm text-fg-tertiary'>
          These properties will be created when the import runs. Review their
          names and types, or expand to edit.
        </p>
      </div>

      {grouped.map(group => (
        <EntityTypeGroup
          key={group.entityTypeName}
          entityTypeName={group.entityTypeName}
          properties={group.properties}
          onPropertyChange={onPropertyChange}
          fieldTypeItems={fieldTypeItems}
          fieldTypesLoading={fieldTypesLoading}
          onGenerateOptions={onGenerateOptions}
          initialExpandedProperty={initialExpandedProperty}
          initialExpandedEntityType={resolvedExpandedEntityType}
          onNavigateToFile={onNavigateToFile}
        />
      ))}
    </div>
  );
}
